import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, createVNode as _createVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-43bf1952"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "subaccount-select-modal__content" }
const _hoisted_2 = {
  key: 1,
  class: "subaccount-select-modal__empty-state"
}
const _hoisted_3 = {
  key: 0,
  class: "subaccount-select-modal__shadow subaccount-select-modal__shadow--bottom"
}

import { computed, inject, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import BigNumber from '@exchange/helpers/bignumber';
import usePromise from '@exchange/libs/composables/shared/src/lib/usePromise';
import { modalService } from '@exchange/libs/modals/src';
import { subaccountsService, SubaccountType, type Subaccount } from '@exchange/libs/trading-accounts/service/src';
import { getSubaccountCreateInfo } from '@exchange/libs/trading-accounts/ui/src/lib/components/subaccount-create-flow';
import { currencyService, type CurrencyModel } from '@exchange/libs/utils/currency/src';


export default /*#__PURE__*/_defineComponent({
  __name: 'SubaccountSelectModal',
  props: {
  isFuturesMarket: { type: Boolean, required: true },
},
  emits: ["selectSubaccount", "cancelModal"],
  setup(__props, { emit: __emit }) {

const props = __props;

const { defaultFiatCurrency, currencies, fiatPrecision } = currencyService;
const { fetchList: fetchSubaccounts } = subaccountsService;

// Add fetch promise handler
const { createPromise: subaccountsFetchNow } = usePromise(fetchSubaccounts);

const emit = __emit;

const { t } = useI18n({ useScope: 'global' });

const balanceMasked = inject('balanceMasked', ref(false));

// Match AccountListBoard's implementation exactly
const getAccountTotalBalanceInFiat = (account: Subaccount, currenciesList: Dictionary<CurrencyModel> | undefined) => {
  if (!currenciesList) return new BigNumber(0);
  return account.getBalanceTotal(currenciesList).all;
};

const subaccountsList = computed(() => {
  if (!currencies.value) return [];

  return subaccountsService.list.value
    .filter((account) => (props.isFuturesMarket ? account.type === SubaccountType.FUTURES : account.type === SubaccountType.SPOT))
    .sort((a, b) => {
      const balanceA = getAccountTotalBalanceInFiat(a, currencies.value);
      const balanceB = getAccountTotalBalanceInFiat(b, currencies.value);
      return new BigNumber(balanceB).minus(balanceA).toNumber();
    });
});

// Fetch data when modal is mounted
onMounted(() => {
  subaccountsFetchNow();
});

const onAccountClick = async (accountId: string) => {
  await subaccountsService.switchTo(accountId);
  emit('selectSubaccount', accountId);
};

const getAccountTypeLabel = (type: SubaccountType): string => {
  switch (type) {
    case SubaccountType.STANDARD:
      return 'Spot';
    case SubaccountType.INSTANT_TRADE:
      return 'Swap';
    case SubaccountType.SPOT:
      return 'Spot';
    case SubaccountType.FUTURES:
      return 'Futures';
    default:
      return type;
  }
};

// Add ref for scroll container
const scrollContainer = ref<HTMLElement | null>(null);
const showBottomShadow = ref(true);

// Add computed property to check if scrolling is possible
const shouldShowShadow = computed(() => subaccountsList.value.length > 5);

// Update scroll handler to consider both conditions
const handleScroll = (event: Event) => {
  if (!shouldShowShadow.value) {
    showBottomShadow.value = false;
    return;
  }

  const target = event.target as HTMLElement;
  const isAtBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 1;
  showBottomShadow.value = !isAtBottom;
};

const { subaccountCreate, subaccountCreateLoading } = getSubaccountCreateInfo();

const router = useRouter();

const onCreateClick = async () =>
  subaccountCreate(
    {
      showSuccessModal: true,
      switchToNewAccount: true,
      defaultAccountType: props.isFuturesMarket ? SubaccountType.FUTURES : SubaccountType.SPOT,
    },
    router,
  );

return (_ctx: any,_cache: any) => {
  const _component_ot_balance = _resolveComponent("ot-balance")!
  const _component_ot_lozenge = _resolveComponent("ot-lozenge")!
  const _component_x_list_item = _resolveComponent("x-list-item")!
  const _component_ot_spinner = _resolveComponent("ot-spinner")!
  const _component_x_button = _resolveComponent("x-button")!
  const _component_x_modal = _resolveComponent("x-modal")!

  return (_openBlock(), _createBlock(_component_x_modal, {
    class: "subaccount-select-modal",
    title: _unref(t)('Select account'),
    variant: "dark",
    onModalClose: _cache[0] || (_cache[0] = ($event: any) => (emit('cancelModal')))
  }, {
    content: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", {
          ref_key: "scrollContainer",
          ref: scrollContainer,
          class: "subaccount-select-modal__scroll-container",
          onScroll: handleScroll
        }, [
          (_unref(currencies) && subaccountsList.value.length)
            ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(subaccountsList.value, (account, idx) => {
                return (_openBlock(), _createBlock(_component_x_list_item, {
                  key: account.accountId,
                  class: _normalizeClass(["subaccount-select-modal__item", { 'subaccount-select-modal__item--main': idx === 0 }]),
                  title: account.name,
                  onClick: ($event: any) => (onAccountClick(account.accountId))
                }, {
                  subtitle: _withCtx(() => [
                    _createVNode(_component_ot_balance, {
                      value: getAccountTotalBalanceInFiat(account, _unref(currencies)),
                      precision: _unref(fiatPrecision),
                      symbol: _unref(defaultFiatCurrency),
                      masked: _unref(balanceMasked)
                    }, null, 8, ["value", "precision", "symbol", "masked"])
                  ]),
                  "label-additional-content": _withCtx(() => [
                    _createVNode(_component_ot_lozenge, {
                      text: getAccountTypeLabel(account.type),
                      class: "subaccount-select-modal__type-lozenge",
                      filled: false
                    }, null, 8, ["text"])
                  ]),
                  _: 2
                }, 1032, ["class", "title", "onClick"]))
              }), 128))
            : (_unref(currencies) && !subaccountsList.value.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, " You don't have any " + _toDisplayString(props.isFuturesMarket ? 'futures' : 'spot') + " sub accounts. Create one by clicking on the button below ", 1))
              : (_openBlock(), _createBlock(_component_ot_spinner, {
                  key: 2,
                  class: "subaccount-select-modal__spinner"
                }))
        ], 544),
        (shouldShowShadow.value && showBottomShadow.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3))
          : _createCommentVNode("", true)
      ])
    ]),
    buttons: _withCtx(() => [
      _createVNode(_component_x_button, {
        class: "subaccount-select-modal__cancel",
        variant: "primary",
        block: true,
        text: _unref(t)('Create subaccount'),
        loading: _unref(subaccountCreateLoading),
        onClick: onCreateClick
      }, null, 8, ["text", "loading"])
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})